import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { keyframes } from '@emotion/react'
import {
  Box,
  Button,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Stack,
  Icon,
  Flex,
  SimpleGrid,
  useColorModeValue,
  Badge,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Divider
} from '@chakra-ui/react'
import {
  FiTrendingUp,
  FiShield,
  FiZap,
  FiActivity,
  FiTarget,
  FiRepeat,
  FiCheck,
  FiArrowRight
} from 'react-icons/fi'
import { RiRobot2Line } from 'react-icons/ri'
import { BiMoney } from 'react-icons/bi'

// Animation definitions
const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
`

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`

interface FeatureCardProps {
  icon: any
  title: string
  description: string
  delay?: number
}

const FeatureCard: React.FC<FeatureCardProps> = ({
  icon,
  title,
  description,
  delay = 0
}) => {
  return (
    <Box
      bg="white"
      p={8}
      borderRadius="2xl"
      shadow="xl"
      transition="all 0.3s"
      _hover={{
        transform: 'translateY(-10px)',
        shadow: '2xl',
        borderColor: 'purple.500'
      }}
      border="2px solid"
      borderColor="gray.100"
      animation={`${fadeInUp} 0.6s ease-out ${delay}s both`}
    >
      <VStack spacing={4} align="start">
        <Box
          p={4}
          bg="purple.50"
          borderRadius="xl"
          color="purple.500"
          fontSize="2xl"
        >
          <Icon as={icon} w={8} h={8} />
        </Box>
        <Heading size="md" color="gray.800">
          {title}
        </Heading>
        <Text color="gray.600" lineHeight="tall">
          {description}
        </Text>
      </VStack>
    </Box>
  )
}

interface StepCardProps {
  number: string
  title: string
  description: string
  delay?: number
}

const StepCard: React.FC<StepCardProps> = ({
  number,
  title,
  description,
  delay = 0
}) => {
  return (
    <Flex
      direction="column"
      align="center"
      textAlign="center"
      animation={`${fadeInUp} 0.6s ease-out ${delay}s both`}
    >
      <Box
        w="80px"
        h="80px"
        borderRadius="full"
        bg="linear-gradient(135deg, #F6C019 0%, #FFCA28 100%)"
        display="flex"
        alignItems="center"
        justifyContent="center"
        fontSize="3xl"
        fontWeight="bold"
        color="white"
        mb={4}
        shadow="lg"
      >
        {number}
      </Box>
      <Heading size="md" mb={3} color="gray.800">
        {title}
      </Heading>
      <Text color="gray.600" maxW="300px">
        {description}
      </Text>
    </Flex>
  )
}

// Review data
const Landing: React.FC = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [currentReview, setCurrentReview] = useState(0)

  const reviews = [
    {
      emoji: '👨‍💼',
      name: t('Michael Kim'),
      role: t('Office Worker Investor'),
      text: t("I didn't have time to watch charts during work, but now AI trades automatically 24/7 for me. It runs stably even while I sleep.")
    },
    {
      emoji: '👩‍💻',
      name: t('Sarah Park'),
      role: t('Crypto Trader'),
      text: t("I've tried many auto-trading bots, but the AI-based system was the most stable. Built by experts with 20 years of experience, the risk management is excellent and it completely prevents emotional trading.")
    },
    {
      emoji: '🧑‍🎓',
      name: t('James Lee'),
      role: t('Student Investor'),
      text: t("Just started investing and it was great not having to study complex strategies. AI automatically trades with optimal strategies, making it easy for beginners to get started.")
    },
    {
      emoji: '👨‍💼',
      name: t('David Choi'),
      role: t('Freelancer'),
      text: t("I couldn't believe this quality was free. No hidden costs and transparent operation builds trust. Highly recommended!")
    },
    {
      emoji: '👩‍⚕️',
      name: t('Emily Jung'),
      role: t('Healthcare Worker'),
      text: t('Working night shifts made it hard to catch market timing, but AI trades automatically 24/7 at optimal times, which is really convenient.')
    },
    {
      emoji: '👨‍🔧',
      name: t('Kevin Kang'),
      role: t('Small Business Owner'),
      text: t("I'm busy running my business, but AI trades automatically so I save a lot of time. Plus it's completely free, so I could start without any burden.")
    }
  ]

  useEffect(() => {
    // Auto transition reviews (every 5 seconds)
    const interval = setInterval(() => {
      setCurrentReview((prev) => (prev + 1) % reviews.length)
    }, 5000)
    return () => clearInterval(interval)
  }, [])

  const handleGetStarted = () => {
    navigate('/home')
  }

  return (
    <Box bg="gray.50" minH="100vh">
      {/* Hero Section */}
      <Box
        position="relative"
        overflow="hidden"
        bg="linear-gradient(135deg, #1a202c 0%, #2d3748 100%)"
        pt={{ base: 20, md: 32 }}
        pb={{ base: 20, md: 32 }}
      >
        {/* Video Background */}
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          overflow="hidden"
        >
          <video
            autoPlay
            loop
            muted
            playsInline
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: 0.15,
              filter: 'brightness(0.7)'
            }}
          >
            <source src="/videos/robot.mp4" type="video/mp4" />
          </video>
        </Box>

        {/* Background decoration */}
        <Box
          position="absolute"
          top="-10%"
          right="-5%"
          w="500px"
          h="500px"
          borderRadius="full"
          bg="purple.500"
          opacity="0.1"
          filter="blur(100px)"
          animation={`${float} 6s ease-in-out infinite`}
        />
        <Box
          position="absolute"
          bottom="-10%"
          left="-5%"
          w="400px"
          h="400px"
          borderRadius="full"
          bg="purple.300"
          opacity="0.1"
          filter="blur(100px)"
          animation={`${float} 8s ease-in-out infinite`}
        />

        <Container maxW="container.xl" position="relative" zIndex={1}>
          <VStack spacing={8} textAlign="center">
            <Badge
              colorScheme="purple"
              fontSize="sm"
              px={4}
              py={2}
              borderRadius="full"
              animation={`${fadeInUp} 0.6s ease-out`}
            >
              {t('⚡ AI Auto-Trading System Developed by 20-Year Expert Team')}
            </Badge>

            <Heading
              fontSize={{ base: '4xl', md: '6xl', lg: '7xl' }}
              fontWeight="bold"
              color="white"
              lineHeight="1.2"
              wordBreak="keep-all"
              animation={`${fadeInUp} 0.6s ease-out 0.2s both`}
            >
              {t('AI Analyzes and')}
              <br />
              <Text
                as="span"
                bgGradient="linear(to-r, purple.400, purple.500)"
                bgClip="text"
              >
                {t('Operates Automatically & Stably')}
              </Text>
            </Heading>

            <Text
              fontSize={{ base: 'lg', md: '2xl' }}
              color="gray.300"
              maxW="800px"
              wordBreak="keep-all"
              animation={`${fadeInUp} 0.6s ease-out 0.4s both`}
            >
              {t('AI-powered smart strategy for 24/7 automated trading')}
              <br />
              {t('Minimized risk • Fully automated')}
            </Text>

            <VStack
              spacing={3}
              animation={`${fadeInUp} 0.6s ease-out 0.6s both`}
            >
              <HStack spacing={4}>
                <Button
                  size="lg"
                  h="60px"
                  px={8}
                  fontSize="xl"
                  colorScheme="purple"
                  bg="purple.500"
                  color="white"
                  _hover={{
                    bg: 'purple.600',
                    transform: 'translateY(-2px)',
                    shadow: '2xl'
                  }}
                  rightIcon={<FiArrowRight />}
                  onClick={handleGetStarted}
                  shadow="2xl"
                >
                  {t('Start for Free')}
                </Button>
                {/* <Button
                  size="lg"
                  h="60px"
                  px={8}
                  fontSize="xl"
                  variant="outline"
                  color="white"
                  borderColor="white"
                  _hover={{
                    bg: 'whiteAlpha.200',
                    transform: 'translateY(-2px)'
                  }}
                >
                  Learn More
                </Button> */}
              </HStack>
            </VStack>

            {/* Stats */}
            <SimpleGrid
              columns={{ base: 1, md: 3 }}
              spacing={8}
              mt={16}
              w="full"
              animation={`${fadeInUp} 0.6s ease-out 0.8s both`}
            >
              <Stat textAlign="center">
                <StatNumber fontSize="4xl" color="white" fontWeight="bold">
                  1,000,000+
                </StatNumber>
                <StatHelpText fontSize="md" color="gray.300">
                  {t('Total Transactions')}
                </StatHelpText>
              </Stat>
              <Stat textAlign="center">
                <StatNumber fontSize="4xl" color="white" fontWeight="bold">
                  100%
                </StatNumber>
                <StatHelpText fontSize="md" color="gray.300">
                  {t('Free')}
                </StatHelpText>
              </Stat>
              <Stat textAlign="center">
                <StatNumber fontSize="4xl" color="white" fontWeight="bold">
                  24/7
                </StatNumber>
                <StatHelpText fontSize="md" color="gray.300">
                  {t('Non-stop Auto Trading')}
                </StatHelpText>
              </Stat>
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* Core Value Proposition - 5 Keywords */}
      <Box py={{ base: 16, md: 24 }} bg="white">
        <Container maxW="container.xl">
          <VStack spacing={16}>
            {/* 1. Free */}
            <SimpleGrid
              columns={{ base: 1, md: 2 }}
              spacing={12}
              alignItems="center"
              w="full"
            >
              <Box order={{ base: 2, md: 1 }}>
                <Badge colorScheme="green" fontSize="md" px={4} py={2} mb={4}>
                  {t('💰 100% Free')}
                </Badge>
                <Heading size="2xl" mb={6} color="gray.800">
                  {t('Use it free forever')}
                </Heading>
                <Text fontSize="lg" color="gray.600" mb={6} lineHeight="tall">
                  {t('Other auto-trading services charge hundreds of dollars monthly.')}{' '}
                  {t('But ours is')}{' '}
                  <Text as="span" fontWeight="bold" color="purple.600">
                    {t('permanently free')}
                  </Text>
                  {t('. No hidden costs, no premium upgrades. All features are unlimited with only Gate.io exchange fees.')}
                </Text>
                <HStack spacing={4}>
                  <VStack align="start" spacing={2}>
                    <HStack>
                      <Icon as={FiCheck} color="green.500" />
                      <Text fontWeight="semibold">{t('No signup fee')}</Text>
                    </HStack>
                    <HStack>
                      <Icon as={FiCheck} color="green.500" />
                      <Text fontWeight="semibold">{t('No monthly fee')}</Text>
                    </HStack>
                  </VStack>
                  <VStack align="start" spacing={2}>
                    <HStack>
                      <Icon as={FiCheck} color="green.500" />
                      <Text fontWeight="semibold">{t('No hidden costs')}</Text>
                    </HStack>
                    <HStack>
                      <Icon as={FiCheck} color="green.500" />
                      <Text fontWeight="semibold">{t('All features unlimited')}</Text>
                    </HStack>
                  </VStack>
                </HStack>
              </Box>
              <Box
                order={{ base: 1, md: 2 }}
                h="400px"
                borderRadius="2xl"
                overflow="hidden"
                shadow="xl"
              >
                <img
                  src="/images/profit/free.png"
                  alt="Free Service"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
            </SimpleGrid>

            <Divider />

            {/* 2. 24/7 Auto Trading */}
            <SimpleGrid
              columns={{ base: 1, md: 2 }}
              spacing={12}
              alignItems="center"
              w="full"
            >
              <Box h="400px" borderRadius="2xl" overflow="hidden" shadow="xl">
                <img
                  src="/images/profit/sleeping.png"
                  alt="24/7 Auto Trading"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
              <Box>
                <Badge colorScheme="blue" fontSize="md" px={4} py={2} mb={4}>
                  {t('⏰ 24-hour Non-stop Operation')}
                </Badge>
                <Heading size="2xl" mb={6} color="gray.800">
                  {t('Stable operation even while you sleep')}
                </Heading>
                <Text fontSize="lg" color="gray.600" mb={6} lineHeight="tall">
                  {t("When you're at work, sleeping, or spending time with family, AI")}{' '}
                  <Text as="span" fontWeight="bold" color="blue.600">
                    {t('monitors the market 24 hours')}
                  </Text>
                  {t('. It automatically executes trades without missing golden opportunities. No more being tied to charts.')}
                </Text>
                <VStack
                  align="start"
                  spacing={3}
                  bg="blue.50"
                  p={6}
                  borderRadius="xl"
                >
                  <HStack>
                    <Icon as={FiCheck} color="blue.500" />
                    <Text>
                      {t('Catches optimal timing even at night')}
                    </Text>
                  </HStack>
                  <HStack>
                    <Icon as={FiCheck} color="blue.500" />
                    <Text>
                      {t('Stable operation during vacation')}
                    </Text>
                  </HStack>
                  <HStack>
                    <Icon as={FiCheck} color="blue.500" />
                    <Text>
                      {t('Auto trading while working')}
                    </Text>
                  </HStack>
                </VStack>
              </Box>
            </SimpleGrid>

            <Divider />

            {/* 3. AI Auto Trading */}
            <SimpleGrid
              columns={{ base: 1, md: 2 }}
              spacing={12}
              alignItems="center"
              w="full"
            >
              <Box order={{ base: 2, md: 1 }}>
                <Badge colorScheme="purple" fontSize="md" px={4} py={2} mb={4}>
                  {t('🤖 AI Strategy by Experts')}
                </Badge>
                <Heading size="2xl" mb={6} color="gray.800">
                  {t('System designed by 20-year veteran')}
                </Heading>
                <Text fontSize="lg" color="gray.600" mb={6} lineHeight="tall">
                  <Text as="span" fontWeight="bold" color="purple.600">
                    A trader with 20 years of experience in global financial markets{' '}
                  </Text>
                  and senior developers from global companies built this together. 
                  Combining experience managing billions in assets with cutting-edge AI technology,
                  it makes{' '}
                  <Text as="span" fontWeight="bold" color="purple.600">
                    emotionless, data-driven decisions
                  </Text>{' '}
                  to enter and exit at optimal timing.
                </Text>
                <SimpleGrid columns={2} spacing={4}>
                  <Box
                    bg="purple.50"
                    p={4}
                    borderRadius="xl"
                    textAlign="center"
                  >
                    <Text fontSize="3xl" fontWeight="bold" color="purple.600">
                      {t('Stable')}
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      {t('Operating System')}
                    </Text>
                  </Box>
                  <Box
                    bg="purple.50"
                    p={4}
                    borderRadius="xl"
                    textAlign="center"
                  >
                    <Text fontSize="3xl" fontWeight="bold" color="purple.600">
                      98.5%
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      {t('System Accuracy')}
                    </Text>
                  </Box>
                  <Box
                    bg="purple.50"
                    p={4}
                    borderRadius="xl"
                    textAlign="center"
                  >
                    <Text fontSize="3xl" fontWeight="bold" color="purple.600">
                      1,000,000+
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      {t('Total Transactions')}
                    </Text>
                  </Box>
                  <Box
                    bg="purple.50"
                    p={4}
                    borderRadius="xl"
                    textAlign="center"
                  >
                    <Text fontSize="3xl" fontWeight="bold" color="purple.600">
                      20+
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      {t('Years of Experience')}
                    </Text>
                  </Box>
                </SimpleGrid>
              </Box>
              <Box
                order={{ base: 1, md: 2 }}
                h="400px"
                borderRadius="2xl"
                overflow="hidden"
                shadow="xl"
              >
                <img
                  src="/images/profit/brain.png"
                  alt="AI Auto Trading"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
            </SimpleGrid>

            <Divider />

            {/* 4. Simplicity */}
            <SimpleGrid
              columns={{ base: 1, md: 2 }}
              spacing={12}
              alignItems="center"
              w="full"
            >
              <Box h="400px" borderRadius="2xl" overflow="hidden" shadow="xl">
                <img
                  src="/images/profit/simple.png"
                  alt="Easy Start"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
              <Box>
                <Badge colorScheme="purple" fontSize="md" px={4} py={2} mb={4}>
                  {t('⚡ Start in 3 minutes')}
                </Badge>
                <Heading size="2xl" mb={6} color="gray.800">
                  {t('Anyone can start easily')}
                </Heading>
                <Text fontSize="lg" color="gray.600" mb={6} lineHeight="tall">
                  {t('No complex setup or programming knowledge needed.')}{' '}
                  <Text as="span" fontWeight="bold" color="purple.600">
                    {t('Just 3 steps')}
                  </Text>{' '}
                  {t('and you can start AI auto-trading right away. Intuitive interface that even beginners can follow easily.')}
                </Text>
                <VStack align="start" spacing={4}>
                  <HStack>
                    <Box
                      w="40px"
                      h="40px"
                      borderRadius="full"
                      bg="purple.100"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      fontWeight="bold"
                      color="purple.600"
                    >
                      1
                    </Box>
                    <Text>
                      <strong>{t('API Connect')}</strong> - {t('Enter Gate.io key (1 min)')}
                    </Text>
                  </HStack>
                  <HStack>
                    <Box
                      w="40px"
                      h="40px"
                      borderRadius="full"
                      bg="purple.100"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      fontWeight="bold"
                      color="purple.600"
                    >
                      2
                    </Box>
                    <Text>
                      <strong>{t('Set Seed')}</strong> - {t('Enter your capital (1 min)')}
                    </Text>
                  </HStack>
                  <HStack>
                    <Box
                      w="40px"
                      h="40px"
                      borderRadius="full"
                      bg="purple.100"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      fontWeight="bold"
                      color="purple.600"
                    >
                      3
                    </Box>
                    <Text>
                      <strong>{t('Start Button')}</strong> - {t('Done with one click (1 sec)')}
                    </Text>
                  </HStack>
                </VStack>
              </Box>
            </SimpleGrid>

            <Divider />

            {/* 5. Safety */}
            <SimpleGrid
              columns={{ base: 1, md: 2 }}
              spacing={12}
              alignItems="center"
              w="full"
            >
              <Box order={{ base: 2, md: 1 }}>
                <Badge colorScheme="green" fontSize="md" px={4} py={2} mb={4}>
                  {t('🔒 Secure Trading')}
                </Badge>
                <Heading size="2xl" mb={6} color="gray.800">
                  {t('Your assets are perfectly protected')}
                </Heading>
                <Text fontSize="lg" color="gray.600" mb={6} lineHeight="tall">
                  {t('All assets are safely stored on Gate.io exchange. We only use')}{' '}
                  <Text as="span" fontWeight="bold" color="green.600">
                    {t('APIs without withdrawal permission')}
                  </Text>
                  {t(', so only trading is possible and withdrawal is not. Your API keys are protected with cutting-edge encryption, and real-time monitoring immediately detects any anomalies.')}
                </Text>
                <SimpleGrid columns={2} spacing={4}>
                  <VStack p={4} bg="green.50" borderRadius="xl">
                    <Icon as={FiShield} w={8} h={8} color="green.500" />
                    <Text
                      fontSize="sm"
                      fontWeight="semibold"
                      textAlign="center"
                    >
                      {t('Encrypted API Key Storage')}
                    </Text>
                  </VStack>
                  <VStack p={4} bg="green.50" borderRadius="xl">
                    <Icon as={FiCheck} w={8} h={8} color="green.500" />
                    <Text
                      fontSize="sm"
                      fontWeight="semibold"
                      textAlign="center"
                    >
                      {t('No Withdrawal Permission')}
                    </Text>
                  </VStack>
                  <VStack p={4} bg="green.50" borderRadius="xl">
                    <Icon as={FiActivity} w={8} h={8} color="green.500" />
                    <Text
                      fontSize="sm"
                      fontWeight="semibold"
                      textAlign="center"
                    >
                      {t('Real-time Monitoring')}
                    </Text>
                  </VStack>
                  <VStack p={4} bg="green.50" borderRadius="xl">
                    <Icon as={FiZap} w={8} h={8} color="green.500" />
                    <Text
                      fontSize="sm"
                      fontWeight="semibold"
                      textAlign="center"
                    >
                      {t('Instant Stop Anytime')}
                    </Text>
                  </VStack>
                </SimpleGrid>
              </Box>
              <Box
                order={{ base: 1, md: 2 }}
                h="400px"
                borderRadius="2xl"
                overflow="hidden"
                shadow="xl"
              >
                <img
                  src="/images/profit/shield.png"
                  alt="Secure Protection"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* Recommended For */}
      <Box py={{ base: 16, md: 24 }} bg="gray.50">
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center" mb={16}>
            <Badge
              colorScheme="purple"
              fontSize="sm"
              px={4}
              py={2}
              borderRadius="full"
            >
              {t('Recommended For')}
            </Badge>
            <Heading size="2xl" color="gray.800">
              {t('Perfect for these users')}
            </Heading>
            <Text fontSize="xl" color="gray.600" maxW="600px">
              {t("Anyone can use it easily, but it's especially optimized for these users")}
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
            <Box
              bg="white"
              p={8}
              borderRadius="2xl"
              shadow="md"
              border="2px solid"
              borderColor="gray.100"
            >
              <VStack spacing={4}>
                <Text fontSize="5xl">👔</Text>
                <Heading size="md" color="gray.800">
                  {t('Busy Office Workers')}
                </Heading>
                <Text color="gray.600" textAlign="center">
                  {t("Even if you can't watch charts during work, AI trades automatically 24 hours a day")}
                </Text>
              </VStack>
            </Box>

            <Box
              bg="white"
              p={8}
              borderRadius="2xl"
              shadow="md"
              border="2px solid"
              borderColor="gray.100"
            >
              <VStack spacing={4}>
                <Text fontSize="5xl">🎓</Text>
                <Heading size="md" color="gray.800">
                  {t('Investment Beginners')}
                </Heading>
                <Text color="gray.600" textAlign="center">
                  {t("With AI's professional analysis, you can operate stably without studying complex strategies")}
                </Text>
              </VStack>
            </Box>

            <Box
              bg="white"
              p={8}
              borderRadius="2xl"
              shadow="md"
              border="2px solid"
              borderColor="gray.100"
            >
              <VStack spacing={4}>
                <Text fontSize="5xl">😫</Text>
                <Heading size="md" color="gray.800">
                  {t('Emotional Traders')}
                </Heading>
                <Text color="gray.600" textAlign="center">
                  {t("AI's emotionless decisions, without fear or greed, aim for stable operation")}
                </Text>
              </VStack>
            </Box>

            <Box
              bg="white"
              p={8}
              borderRadius="2xl"
              shadow="md"
              border="2px solid"
              borderColor="gray.100"
            >
              <VStack spacing={4}>
                <Text fontSize="5xl">⏰</Text>
                <Heading size="md" color="gray.800">
                  {t('Time-strapped Users')}
                </Heading>
                <Text color="gray.600" textAlign="center">
                  {t('No need to watch charts for hours daily - AI handles everything automatically')}
                </Text>
              </VStack>
            </Box>

            <Box
              bg="white"
              p={8}
              borderRadius="2xl"
              shadow="md"
              border="2px solid"
              borderColor="gray.100"
            >
              <VStack spacing={4}>
                <Text fontSize="5xl">💼</Text>
                <Heading size="md" color="gray.800">
                  {t('Portfolio Diversifiers')}
                </Heading>
                <Text color="gray.600" textAlign="center">
                  {t('Secure a stable automated operation system alongside manual trading')}
                </Text>
              </VStack>
            </Box>

            <Box
              bg="white"
              p={8}
              borderRadius="2xl"
              shadow="md"
              border="2px solid"
              borderColor="gray.100"
            >
              <VStack spacing={4}>
                <Text fontSize="5xl">🌙</Text>
                <Heading size="md" color="gray.800">
                  {t('Night Shift Workers')}
                </Heading>
                <Text color="gray.600" textAlign="center">
                  {t('Whether sleeping during day or working at night, AI never misses optimal timing')}
                </Text>
              </VStack>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box py={{ base: 16, md: 24 }} bg="white">
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center" mb={16}>
            <Badge
              colorScheme="purple"
              fontSize="sm"
              px={4}
              py={2}
              borderRadius="full"
            >
              {t('Key Features')}
            </Badge>
            <Heading size="2xl" color="gray.800">
              {t('Why AI Auto Trading?')}
            </Heading>
            <Text fontSize="xl" color="gray.600" maxW="600px">
              {t('AI-based smart strategy that surpasses the limits of traditional manual trading')}
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8}>
            <FeatureCard
              icon={RiRobot2Line}
              title={t('AI Strategy by Experts')}
              description={t('AI algorithm designed by veteran traders with 20 years of experience analyzes the market and automatically executes trades at optimal timing')}
              delay={0}
            />
            <FeatureCard
              icon={FiRepeat}
              title={t('Smart Position Management')}
              description={t('AI analyzes market conditions to automatically manage optimal positions and capture profit opportunities')}
              delay={0.1}
            />
            <FeatureCard
              icon={BiMoney}
              title={t('Stable Trading Operations')}
              description={t('AI minimizes risk and supports sustainable trading through stable operations')}
              delay={0.2}
            />
            <FeatureCard
              icon={FiZap}
              title={t('Auto Take Profit/Stop Loss')}
              description={t('Auto take profit when reaching target, with loss limit features for 24/7 worry-free operation')}
              delay={0.3}
            />
            <FeatureCard
              icon={FiTarget}
              title={t('Fully Automated System')}
              description={t('From signal detection to entry and exit, everything is automated. Stable operation even while you sleep')}
              delay={0.4}
            />
            <FeatureCard
              icon={FiShield}
              title={t('Secure Trade Protection')}
              description={t('Duplicate entry prevention and smart re-entry system protect your trades safely')}
              delay={0.5}
            />
          </SimpleGrid>
        </Container>
      </Box>

      {/* How It Works Section */}
      <Box py={{ base: 16, md: 24 }} bg="white">
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center" mb={16}>
            <Badge
              colorScheme="purple"
              fontSize="sm"
              px={4}
              py={2}
              borderRadius="full"
            >
              {t('Getting Started')}
            </Badge>
            <Heading size="2xl" color="gray.800">
              {t('Start AI Auto Trading in 3 Steps')}
            </Heading>
            <Text fontSize="xl" color="gray.600" maxW="600px">
              {t('Stable operation by AI without studying complex strategies')}
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={16} mb={16}>
            <StepCard
              number="1"
              title={t('Connect Account')}
              description={t('Register your Gate.io API key and complete trading settings')}
              delay={0}
            />
            <StepCard
              number="2"
              title={t('Trading Settings')}
              description={t('Set your seed amount and AI will automatically apply optimal strategy')}
              delay={0.2}
            />
            <StepCard
              number="3"
              title={t('Start Auto Trading')}
              description={t('24/7 auto trading starts with just one button click')}
              delay={0.4}
            />
          </SimpleGrid>

          <VStack
            spacing={6}
            align="start"
            bg="gray.50"
            p={8}
            borderRadius="2xl"
          >
            <Heading size="md" color="gray.800">
              {t('✨ How AI Auto Trading Works')}
            </Heading>
            <Stack spacing={4}>
              <HStack>
                <Icon as={FiCheck} color="green.500" w={5} h={5} />
                <Text color="gray.700">
                  <strong>{t('AI Signal Analysis:')}</strong> {t('Machine learning algorithm analyzes market in real-time to automatically detect optimal entry points')}
                </Text>
              </HStack>
              <HStack>
                <Icon as={FiCheck} color="green.500" w={5} h={5} />
                <Text color="gray.700">
                  <strong>{t('Smart Risk Management:')}</strong> {t('AI automatically adjusts position size and timing according to market volatility')}
                </Text>
              </HStack>
              <HStack>
                <Icon as={FiCheck} color="green.500" w={5} h={5} />
                <Text color="gray.700">
                  <strong>{t('Auto Portfolio Management:')}</strong> {t('Diversify investments across multiple coins to minimize risk and ensure stable operation')}
                </Text>
              </HStack>
              <HStack>
                <Icon as={FiCheck} color="green.500" w={5} h={5} />
                <Text color="gray.700">
                  <strong>{t('Continuous Strategy Optimization:')}</strong> {t('Learn from market data to improve strategies in real-time')}
                </Text>
              </HStack>
            </Stack>
          </VStack>
        </Container>
      </Box>

      {/* Comparison Section */}
      <Box py={{ base: 16, md: 24 }} bg="gray.50">
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center" mb={16}>
            <Badge
              colorScheme="purple"
              fontSize="sm"
              px={4}
              py={2}
              borderRadius="full"
            >
              {t('Comparison')}
            </Badge>
            <Heading size="2xl" color="gray.800">
              {t('Manual Trading vs AI Auto Trading')}
            </Heading>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
            {/* Traditional Method */}
            <Box
              bg="white"
              p={8}
              borderRadius="2xl"
              shadow="md"
              border="2px solid"
              borderColor="red.200"
            >
              <VStack align="start" spacing={4}>
                <Badge colorScheme="red" fontSize="md" px={3} py={1}>
                  {t('❌ Manual Trading')}
                </Badge>
                <Stack spacing={3} w="full">
                  <HStack>
                    <Icon as={FiCheck} color="red.400" w={5} h={5} />
                    <Text color="gray.700">{t('Requires 24/7 chart monitoring')}</Text>
                  </HStack>
                  <HStack>
                    <Icon as={FiCheck} color="red.400" w={5} h={5} />
                    <Text color="gray.700">{t('Risk of loss from emotional decisions')}</Text>
                  </HStack>
                  <HStack>
                    <Icon as={FiCheck} color="red.400" w={5} h={5} />
                    <Text color="gray.700">{t('Strategy study and experience required')}</Text>
                  </HStack>
                  <HStack>
                    <Icon as={FiCheck} color="red.400" w={5} h={5} />
                    <Text color="gray.700">{t('Missed opportunities and delayed response')}</Text>
                  </HStack>
                  <HStack>
                    <Icon as={FiCheck} color="red.400" w={5} h={5} />
                    <Text color="gray.700">
                      {t('Unstable returns and high loss potential')}
                    </Text>
                  </HStack>
                </Stack>
              </VStack>
            </Box>

            {/* AI Auto Trading */}
            <Box
              bg="white"
              p={8}
              borderRadius="2xl"
              shadow="xl"
              border="2px solid"
              borderColor="purple.500"
              position="relative"
            >
              <Badge
                position="absolute"
                top="-3"
                right="4"
                colorScheme="purple"
                fontSize="sm"
                px={3}
                py={1}
              >
                {t('⭐ RECOMMENDED')}
              </Badge>
              <VStack align="start" spacing={4}>
                <Badge colorScheme="green" fontSize="md" px={3} py={1}>
                  {t('✅ AI Auto Trading')}
                </Badge>
                <Stack spacing={3} w="full">
                  <HStack>
                    <Icon as={FiCheck} color="green.500" w={5} h={5} />
                    <Text color="gray.700" fontWeight="semibold">
                      {t('AI monitors market 24 hours')}
                    </Text>
                  </HStack>
                  <HStack>
                    <Icon as={FiCheck} color="green.500" w={5} h={5} />
                    <Text color="gray.700" fontWeight="semibold">
                      {t('Emotionless decisions for stable operation')}
                    </Text>
                  </HStack>
                  <HStack>
                    <Icon as={FiCheck} color="green.500" w={5} h={5} />
                    <Text color="gray.700" fontWeight="semibold">
                      {t('No strategy study needed, AI handles everything')}
                    </Text>
                  </HStack>
                  <HStack>
                    <Icon as={FiCheck} color="green.500" w={5} h={5} />
                    <Text color="gray.700" fontWeight="semibold">
                      {t('Instant auto response at optimal timing')}
                    </Text>
                  </HStack>
                  <HStack>
                    <Icon as={FiCheck} color="green.500" w={5} h={5} />
                    <Text color="gray.700" fontWeight="semibold">
                      {t('Stable and sustainable operation')}
                    </Text>
                  </HStack>
                </Stack>
              </VStack>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Testimonials Section - Slider */}
      <Box py={{ base: 16, md: 24 }} bg="white" overflow="hidden">
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center" mb={16}>
            <Badge
              colorScheme="purple"
              fontSize="sm"
              px={4}
              py={2}
              borderRadius="full"
            >
              {t('User Reviews')}
            </Badge>
            <Heading size="2xl" color="gray.800">
              {t('Real User Experiences')}
            </Heading>
            <Text fontSize="xl" color="gray.600" maxW="600px">
              {t('Stories from users who experienced successful trading with AI auto trading')}
            </Text>
          </VStack>

          {/* Slider Container */}
          <Box position="relative" maxW="900px" mx="auto">
            <Box
              display="flex"
              transition="transform 0.5s ease-in-out"
              transform={`translateX(-${currentReview * 100}%)`}
            >
              {reviews.map((review, index) => (
                <Box key={index} minW="100%" px={4}>
                  <Box
                    bg="gray.50"
                    p={10}
                    borderRadius="2xl"
                    shadow="xl"
                    border="2px solid"
                    borderColor="purple.100"
                  >
                    <VStack align="center" spacing={6} textAlign="center">
                      <Box
                        w="80px"
                        h="80px"
                        borderRadius="full"
                        bg="purple.100"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Text fontSize="4xl">{review.emoji}</Text>
                      </Box>
                      <VStack spacing={1}>
                        <Text fontWeight="bold" fontSize="xl" color="gray.800">
                          {review.name}
                        </Text>
                        <Text fontSize="md" color="gray.500">
                          {review.role}
                        </Text>
                      </VStack>
                      <Text
                        color="gray.700"
                        fontSize="lg"
                        lineHeight="tall"
                        maxW="600px"
                      >
                        "{review.text}"
                      </Text>
                      <HStack spacing={1}>
                        {[...Array(5)].map((_, i) => (
                          <Text key={i} color="purple.500" fontSize="xl">
                            ⭐
                          </Text>
                        ))}
                      </HStack>
                    </VStack>
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Dots Indicator */}
            <HStack justify="center" spacing={2} mt={8}>
              {reviews.map((_, index) => (
                <Box
                  key={index}
                  w={currentReview === index ? '32px' : '8px'}
                  h="8px"
                  borderRadius="full"
                  bg={currentReview === index ? 'purple.500' : 'gray.300'}
                  transition="all 0.3s"
                  cursor="pointer"
                  onClick={() => setCurrentReview(index)}
                />
              ))}
            </HStack>

            {/* Navigation Arrows */}
            <Button
              position="absolute"
              left="-60px"
              top="50%"
              transform="translateY(-50%)"
              onClick={() =>
                setCurrentReview(
                  (prev) => (prev - 1 + reviews.length) % reviews.length
                )
              }
              borderRadius="full"
              w="50px"
              h="50px"
              bg="white"
              shadow="lg"
              _hover={{ bg: 'purple.50' }}
              display={{ base: 'none', lg: 'flex' }}
              alignItems="center"
              justifyContent="center"
            >
              ‹
            </Button>
            <Button
              position="absolute"
              right="-60px"
              top="50%"
              transform="translateY(-50%)"
              onClick={() =>
                setCurrentReview((prev) => (prev + 1) % reviews.length)
              }
              borderRadius="full"
              w="50px"
              h="50px"
              bg="white"
              shadow="lg"
              _hover={{ bg: 'purple.50' }}
              display={{ base: 'none', lg: 'flex' }}
              alignItems="center"
              justifyContent="center"
            >
              ›
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Security Section */}
      <Box py={{ base: 16, md: 24 }} bg="gray.50">
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center" mb={16}>
            <Badge
              colorScheme="purple"
              fontSize="sm"
              px={4}
              py={2}
              borderRadius="full"
            >
              {t('Security & Reliability')}
            </Badge>
            <Heading size="2xl" color="gray.800">
              {t('Your assets protected safely')}
            </Heading>
            <Text fontSize="xl" color="gray.600" maxW="600px">
              {t('We provide a secure trading environment with strict security systems')}
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
            <Box bg="white" p={8} borderRadius="2xl" shadow="md">
              <VStack align="start" spacing={4}>
                <Box
                  w="60px"
                  h="60px"
                  borderRadius="xl"
                  bg="purple.50"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Icon as={FiShield} w={8} h={8} color="purple.500" />
                </Box>
                <Heading size="md" color="gray.800">
                  {t('Encrypted API Key Storage')}
                </Heading>
                <Text color="gray.700" lineHeight="tall">
                  {t('User Gate.io API keys are safely stored with cutting-edge encryption. With no withdrawal permission, only trading is possible, perfectly protecting your assets.')}
                </Text>
              </VStack>
            </Box>

            <Box bg="white" p={8} borderRadius="2xl" shadow="md">
              <VStack align="start" spacing={4}>
                <Box
                  w="60px"
                  h="60px"
                  borderRadius="xl"
                  bg="purple.50"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Icon as={FiCheck} w={8} h={8} color="purple.500" />
                </Box>
                <Heading size="md" color="gray.800">
                  {t('Real-time Monitoring')}
                </Heading>
                <Text color="gray.700" lineHeight="tall">
                  {t('All trades are monitored in real-time, with immediate alerts when anomalies are detected. We guarantee a transparent and secure trading environment.')}
                </Text>
              </VStack>
            </Box>

            <Box bg="white" p={8} borderRadius="2xl" shadow="md">
              <VStack align="start" spacing={4}>
                <Box
                  w="60px"
                  h="60px"
                  borderRadius="xl"
                  bg="purple.50"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Icon as={RiRobot2Line} w={8} h={8} color="purple.500" />
                </Box>
                <Heading size="md" color="gray.800">
                  {t('AI Algorithm by Experts')}
                </Heading>
                <Text color="gray.700" lineHeight="tall">
                  {t('Designed in collaboration with veteran traders with 20 years of experience and developers from global IT companies. A stable and reliable system combining battle-tested strategies with cutting-edge AI technology.')}
                </Text>
              </VStack>
            </Box>

            <Box bg="white" p={8} borderRadius="2xl" shadow="md">
              <VStack align="start" spacing={4}>
                <Box
                  w="60px"
                  h="60px"
                  borderRadius="xl"
                  bg="purple.50"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Icon as={FiZap} w={8} h={8} color="purple.500" />
                </Box>
                <Heading size="md" color="gray.800">
                  {t('Stop Anytime')}
                </Heading>
                <Text color="gray.700" lineHeight="tall">
                  {t('You can instantly stop auto trading at any time, and users always have complete control.')}
                </Text>
              </VStack>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box py={{ base: 16, md: 24 }} bg="white">
        <Container maxW="container.lg">
          <VStack spacing={4} textAlign="center" mb={16}>
            <Badge
              colorScheme="purple"
              fontSize="sm"
              px={4}
              py={2}
              borderRadius="full"
            >
              {t('Frequently Asked Questions')}
            </Badge>
            <Heading size="2xl" color="gray.800">
              {t('FAQ')}
            </Heading>
            <Text fontSize="xl" color="gray.600" maxW="600px">
              {t("We've collected frequently asked questions from users")}
            </Text>
          </VStack>

          <VStack spacing={4} align="stretch">
            <Box bg="gray.50" p={8} borderRadius="xl">
              <Heading size="sm" color="gray.800" mb={3}>
                {t('💡 Are profits guaranteed?')}
              </Heading>
              <Text color="gray.700">
                {t('AI auto trading aims for stable operation, but due to the nature of the cryptocurrency market, profits cannot be guaranteed. There may be fluctuations depending on market conditions, and AI is designed to minimize risk. All investments should be made at your own responsibility.')}
              </Text>
            </Box>

            <Box bg="gray.50" p={8} borderRadius="xl">
              <Heading size="sm" color="gray.800" mb={3}>
                {t('💡 Are my assets safe?')}
              </Heading>
              <Text color="gray.700">
                {t('All assets are stored on Gate.io exchange, and we only use API keys without withdrawal permission. Only trading is possible, so your assets are perfectly protected.')}
              </Text>
            </Box>

            <Box bg="gray.50" p={8} borderRadius="xl">
              <Heading size="sm" color="gray.800" mb={3}>
                {t('💡 Can I stop anytime?')}
              </Heading>
              <Text color="gray.700">
                {t('Yes, you can instantly stop auto trading from the dashboard at any time, and you can also manually close any open positions.')}
              </Text>
            </Box>

            <Box bg="gray.50" p={8} borderRadius="xl">
              <Heading size="sm" color="gray.800" mb={3}>
                {t('💡 Which coins are supported?')}
              </Heading>
              <Text color="gray.700">
                {t('Currently only XRP is supported, with more coins planned to be added in the future.')}
              </Text>
            </Box>

            <Box bg="gray.50" p={8} borderRadius="xl">
              <Heading size="sm" color="gray.800" mb={3}>
                {t('💡 What are the fees?')}
              </Heading>
              <Text color="gray.700">
                <Text as="span" fontWeight="bold" color="purple.600">
                  {t('Completely free!')}
                </Text>{' '}
                {t('There is no platform fee other than Gate.io exchange fees, and we are permanently free with absolutely no hidden costs.')}
              </Text>
            </Box>

            <Box
              bg="white"
              p={8}
              borderRadius="xl"
              border="1px solid"
              borderColor="purple.200"
              shadow="sm"
              transition="all 0.2s"
              _hover={{
                shadow: 'md',
                borderColor: 'purple.300'
              }}
            >
              <VStack align="start" spacing={5}>
                <HStack spacing={2}>
                  <Text fontSize="lg">🎁</Text>
                  <Heading size="sm" color="gray.800">
                    {t('Why is it free?')}
                  </Heading>
                </HStack>

                <Text color="gray.700" fontSize="md" lineHeight="tall">
                  {t('Working in financial markets for 20 years, we realized something. All investors should equally enjoy the benefits of AI technology.')}{' '}
                  <Text as="span" fontWeight="bold" color="purple.600">
                    {t('Advanced auto trading technology that was only available to hedge funds and institutional investors')}
                  </Text>{' '}
                  {t('should be available for free to retail investors too. Our team of veteran traders with 20 years of experience and developers from global IT companies has one goal:')}{' '}
                  <Text as="span" fontWeight="bold" color="purple.700">
                    {t('equality of financial opportunities')}
                  </Text>
                  {t('. Your success is our success, and that\'s why we offer this service permanently free.')}
                </Text>
              </VStack>
            </Box>
          </VStack>
        </Container>
      </Box>

      {/* Final CTA Section */}
      <Box
        py={{ base: 16, md: 24 }}
        bg="linear-gradient(135deg, #1a202c 0%, #2d3748 100%)"
        position="relative"
        overflow="hidden"
      >
        <Box
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          w="600px"
          h="600px"
          borderRadius="full"
          bg="purple.500"
          opacity="0.1"
          filter="blur(120px)"
        />

        <Container maxW="container.md" position="relative" zIndex={1}>
          <VStack spacing={8} textAlign="center">
            <Heading
              fontSize={{ base: '3xl', md: '5xl' }}
              color="white"
              fontWeight="bold"
            >
              {t('Start Right Now')}
            </Heading>
            <Text
              fontSize={{ base: 'lg', md: 'xl' }}
              color="gray.300"
              maxW="600px"
            >
              {t('Experience stable operation with AI-based smart auto trading.')}
              <br />
              <Text as="span" color="purple.400" fontWeight="bold">
                {t('Completely free')}
              </Text>
              {t(', start in 3 minutes without studying complex strategies.')}
            </Text>
            <VStack spacing={3}>
              <Button
                size="lg"
                h="70px"
                px={12}
                fontSize="2xl"
                colorScheme="purple"
                bg="purple.500"
                color="white"
                _hover={{
                  bg: 'purple.600',
                  transform: 'scale(1.05)',
                  shadow: '2xl'
                }}
                rightIcon={<FiArrowRight />}
                onClick={handleGetStarted}
                shadow="2xl"
              >
                {t('Start for Free')}
              </Button>
            </VStack>
          </VStack>
        </Container>
      </Box>

      {/* Footer */}
      <Box bg="gray.900" py={8}>
        <Container maxW="container.xl">
          <VStack spacing={4}>
            <Text color="gray.400" fontSize="sm">
              © 2025 FreeRipple. All rights reserved.
            </Text>
            <HStack spacing={6}>
              <Text
                color="gray.500"
                fontSize="sm"
                cursor="pointer"
                _hover={{ color: 'purple.500' }}
              >
                {t('Terms of Service')}
              </Text>
              <Text
                color="gray.500"
                fontSize="sm"
                cursor="pointer"
                _hover={{ color: 'purple.500' }}
              >
                {t('Privacy Policy')}
              </Text>
              <Text
                color="gray.500"
                fontSize="sm"
                cursor="pointer"
                _hover={{ color: 'purple.500' }}
              >
                {t('Support')}
              </Text>
            </HStack>
          </VStack>
        </Container>
      </Box>
    </Box>
  )
}

export default Landing
